import { useEffect, useState, useCallback } from "react";
import { Card, Empty, Input, message, Modal, Pagination } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import DateRangeFilter from "../shared/DateRangeFilter";
import BlueButton from "../shared/BlueButton";
import ProjectTaskTile from "./ProjectTaskTile";
import TaskForm from "../task/TaskForm";

import { createTask, updateTask, getTasks } from "../../api/task";
import { UserRole } from "../../lib/constants";

const { Search } = Input;

export default function ProjectTasks({ project, afterClose, user }) {
    const [loading, setLoading] = useState(false);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [taskFormInstance, setTaskFormInstance] = useState();
    const [tasks, setTasks] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isDisabled, setIsDisabled] = useState(true);
    const [editTask, setEditTask] = useState(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [range, setRange] = useState(null);
    const perPage = 5;

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const result = await getTasks({
                q: searchQuery,
                projectId: project.id,
                perPage,
                page,
                startAt: range?.startDate,
                endAt: range?.endDate,
            });
            setTasks(result.results);
            setTotal(result.total);
            setIsDisabled(false);
        } catch (err) {
            console.error(err);
            message.error("Something went wrong");
        }
        setLoading(false);
    }, [page, project, searchQuery, range])

    useEffect(() => {
        if (project) {
            fetchTasks();
        }
    }, [fetchTasks, project])

    return (
        <Card
            title={<p className="font-medium">Project Tasks</p>}
            extra={
                <div className="flex space-x-4 items-center">
                    <DateRangeFilter
                        onChange={({ startDate, endDate }) => {
                            if (startDate && endDate) {
                                setRange({ startDate, endDate });
                            }
                        }}
                        onClear={() => setRange({ startDate: null, endDate: null })}
                    />
                    <Search
                        placeholder="Search tasks"
                        allowClear
                        onSearch={setSearchQuery}
                        disabled={loading || isDisabled}
                        style={{
                            width: 200,
                            borderRadius: 0,
                        }}
                    />
                    {user?.role !== UserRole.AccountManager && (
                        <BlueButton
                            className="flex items-center"
                            type="default"
                            onClick={() => setIsTaskModalOpen(true)}
                            disabled={isDisabled || loading}
                        >
                            <PlusOutlined /> Add Task
                        </BlueButton>
                    )}
                </div>
            }
        >
            <div className="pt-6">
                {
                    isDisabled ? <EmptyState />
                        : tasks.length > 0 ? (
                            tasks.map((task, index) => {
                                if (task.isActive) {
                                    task["project"] = project.id
                                    return (
                                        <ProjectTaskTile
                                            key={task.id}
                                            id={task.id}
                                            name={task.name}
                                            startDate={task.startDate}
                                            dueDate={task.dueDate}
                                            assignee={task.assignee}
                                            refresh={afterClose}
                                            user={user}
                                            onEdit={() => {
                                                setIsTaskModalOpen(true);
                                                setEditTask(task);
                                            }}
                                        />
                                    );
                                } else return null;
                            })
                        ) : (
                            <EmptyState />
                        )}
            </div>
            {
                !isDisabled && <div className="pb-4">
                    <Pagination
                        align="end"
                        total={total}
                        pageSize={perPage}
                        current={page}
                        onChange={setPage}
                        showTotal={(total) => `Total ${total} tasks`}
                    />
                </div>
            }

            {
                !isDisabled && user?.role !== UserRole.AccountManager &&
                <Modal
                    title="Add Task"
                    open={isTaskModalOpen}
                    onCancel={() => {
                        taskFormInstance?.resetFields();
                        setIsTaskModalOpen(false);
                        setEditTask(null);
                    }}
                    onOk={async () => {
                        const values = await taskFormInstance?.validateFields();
                        if (editTask) {
                            try {
                                await updateTask({ ...editTask, ...values }).then(() => {
                                    message.success("Task updated successfully")
                                    taskFormInstance?.resetFields();
                                    setIsTaskModalOpen(false);
                                    afterClose();
                                });
                            } catch (error) {
                                console.error("Failed:", error);
                                message.error("Failed to update task")
                            }
                        } else {
                            try {
                                await createTask({ project: project.id, ...values }).then(() => {
                                    message.success("Task created successfully")
                                    taskFormInstance?.resetFields();
                                    setIsTaskModalOpen(false);
                                    afterClose();
                                });
                            } catch (error) {
                                console.log("Failed:", error);
                                message.error("Failed to create task")
                            }
                        }

                    }
                    }
                >
                    <TaskForm
                        projectUsers={project.projectUsers?.map((pu) => pu.user)}
                        projectEquipments={project.projectEquipments?.map(
                            (pe) => pe.equipment
                        )}
                        projects={[project]}
                        onFormInstanceReady={(instance) => {
                            setTaskFormInstance(instance)
                            taskFormInstance?.setFieldsValue({ project: project.id });
                        }}
                        initialValues={editTask}
                    />
                </Modal>
            }

        </Card>
    )
}

const EmptyState = () => {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className="my-8" />
}